import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Select, SelectItem, Spinner } from "@heroui/react";
import { R1DataTable } from "../components/R1DataTable";
import {
  buildFundScopeParams,
  buildR1DownloadUrl,
  r1Api,
  type R1FundSelection,
} from "../services/api";
import { DownloadButton } from "@/components/DownloadButton";
import { PortfolioCommentCell } from "../portfolio/ui/PortfolioCommentCell";
import { STEP_CONFIG, STATUS_CONFIG } from "../validation/domain/types";
import type {
  ValidationStep,
  ValidationStatus,
} from "../validation/domain/types";

function buildDownloadUrl(quarter: string | null): string {
  const params = buildFundScopeParams(ALL_FUNDS_SELECTION);
  if (quarter) params.set("quarter_equals", quarter);
  return buildR1DownloadUrl("r1_funds_portfolio", params);
}

type Row = Record<string, unknown>;

function formatDate(value: unknown): string {
  if (!value) return "";
  const d = new Date(String(value));
  if (isNaN(d.getTime())) return String(value);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}`;
}

function Placeholder() {
  return <span className="text-slate-300 text-xs">—</span>;
}

function GroupLabel({
  label,
  bg,
  text,
}: {
  label: string;
  bg: string;
  text: string;
}) {
  return (
    <div
      className={`flex h-full w-full items-center justify-center px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.1em] ${bg} ${text}`}
    >
      {label}
    </div>
  );
}

const CAMPAIGN_COLS: ColumnDef<Row>[] = [
  {
    accessorKey: "r1_gp_id_fonds",
    header: "GP ID",
    size: 100,
    cell: ({ getValue }) => {
      const v = getValue();
      return v !== null && v !== undefined ? String(v) : <Placeholder />;
    },
  },
  {
    accessorKey: "fund_name",
    header: "Fund",
    size: 155,
    cell: ({ getValue }) => {
      const v = getValue();
      return v !== null && v !== undefined ? (
        <span className="font-medium">{String(v)}</span>
      ) : (
        <Placeholder />
      );
    },
  },
  {
    accessorKey: "date_pdf_received",
    header: "PDF received",
    size: 100,
    cell: ({ getValue }) => {
      const v = getValue();
      return v ? formatDate(v) : "";
    },
  },
  {
    accessorKey: "date_trr_received",
    header: "TRR received",
    size: 100,
    cell: ({ getValue }) => {
      const v = getValue();
      return v ? formatDate(v) : "";
    },
  },
  {
    id: "all_files_received",
    header: "All files received",
    size: 120,
    cell: ({ row }) => {
      const pdf = row.original.date_pdf_received;
      const trr = row.original.date_trr_received;
      return pdf && trr ? (
        <span className="font-semibold text-emerald-600">x</span>
      ) : (
        ""
      );
    },
  },
];

const CONFIG_COLS: ColumnDef<Row>[] = [
  {
    accessorKey: "trr_required",
    header: "TRR Required",
    size: 110,
    cell: ({ getValue }) => {
      const v = getValue();
      if (v === null || v === undefined) return <Placeholder />;
      return v ? (
        <span className="font-semibold text-emerald-600">Yes</span>
      ) : (
        <span className="text-slate-400">No</span>
      );
    },
  },
  {
    accessorKey: "first_production",
    header: "First Production",
    size: 120,
    cell: ({ getValue }) => {
      const v = getValue();
      if (v === null || v === undefined) return <Placeholder />;
      return v ? (
        <span className="font-semibold text-emerald-600">Yes</span>
      ) : (
        <span className="text-slate-400">No</span>
      );
    },
  },
  {
    accessorKey: "deadline_nb_days",
    header: "Deadline",
    size: 90,
    cell: ({ getValue }) => {
      const v = getValue();
      return v !== null && v !== undefined ? `${v}d` : <Placeholder />;
    },
  },
];

const EMAIL_COLS: ColumnDef<Row>[] = [
  {
    accessorKey: "date_initial_notification",
    header: "Initial Notif.",
    size: 110,
    cell: ({ getValue }) => {
      const v = getValue();
      return v ? formatDate(v) : "";
    },
  },
  {
    accessorKey: "date_chaser_1",
    header: "Chaser 1",
    size: 85,
    cell: ({ getValue }) => {
      const v = getValue();
      return v ? formatDate(v) : "";
    },
  },
  {
    accessorKey: "date_chaser_2",
    header: "Chaser 2",
    size: 85,
    cell: ({ getValue }) => {
      const v = getValue();
      return v ? formatDate(v) : "";
    },
  },
  {
    accessorKey: "date_escalation",
    header: "Escalation",
    size: 85,
    cell: ({ getValue }) => {
      const v = getValue();
      return v ? formatDate(v) : "";
    },
  },
];

function useColumns(effectiveQuarter: string | null): ColumnDef<Row>[] {
  return useMemo(
    () => [
      {
        id: "group_campaign",
        header: () => (
          <GroupLabel label="Campaign Status" bg="bg-sky-50" text="text-sky-700" />
        ),
        columns: CAMPAIGN_COLS,
      },
      {
        id: "group_config",
        header: () => (
          <GroupLabel label="Configuration" bg="bg-violet-50" text="text-violet-700" />
        ),
        columns: CONFIG_COLS,
      },
      {
        id: "group_emails",
        header: () => (
          <GroupLabel label="Emails" bg="bg-amber-50" text="text-amber-700" />
        ),
        columns: EMAIL_COLS,
      },
      {
        id: "group_qa",
        header: () => (
          <GroupLabel label="Quality Assurance" bg="bg-emerald-50" text="text-emerald-700" />
        ),
        columns: [
          {
            id: "validation_status",
            header: "Validation Status",
            size: 200,
            cell: ({ row }) => {
              const step = row.original.validation_step as ValidationStep | null;
              const status = row.original
                .validation_status as ValidationStatus | null;
              const at = row.original.validation_at as string | null;
              if (!step || !status) return <Placeholder />;
              const stepCfg = STEP_CONFIG[step];
              const statusCfg = STATUS_CONFIG[status];
              return (
                <div
                  className={`inline-flex flex-col gap-0.5 rounded-lg border px-2 py-1 ${stepCfg.color} ${stepCfg.borderColor}`}
                >
                  <span
                    className={`text-[11px] font-semibold ${stepCfg.textColor}`}
                  >
                    {stepCfg.label}
                  </span>
                  <span
                    className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${statusCfg.badgeClass}`}
                  >
                    {statusCfg.icon} {statusCfg.label}
                  </span>
                  {at && (
                    <span className="text-[10px] text-slate-400">
                      {formatDate(at)}
                    </span>
                  )}
                </div>
              );
            },
          },
          {
            id: "nav_mismatch",
            header: "NAV Mismatch PDF / Excel",
            size: 170,
            cell: ({ row }) => {
              const count = row.original.nav_mismatch_count as number | null;
              if (count == null) return <Placeholder />;
              if (count === 0)
                return (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200">
                    No mismatches
                  </span>
                );
              return (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600 ring-1 ring-red-200">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                  {count} mismatch{count > 1 ? "es" : ""}
                </span>
              );
            },
          },
          {
            id: "comment",
            header: "Comments",
            size: 220,
            cell: ({ row }) => {
              const fundId = row.original.fund_id as number | undefined;
              const fundName =
                (row.original.fund_name as string | undefined) ?? "";
              if (!fundId || !effectiveQuarter) return <Placeholder />;
              return (
                <PortfolioCommentCell
                  fundId={fundId}
                  fundName={fundName}
                  quarter={effectiveQuarter}
                  lastCommentMessage={
                    row.original.last_comment_message as string | null
                  }
                  lastCommentUserName={
                    row.original.last_comment_user_name as string | null
                  }
                  lastCommentUserEmail={
                    row.original.last_comment_user_email as string | null
                  }
                  lastCommentAt={row.original.last_comment_at as string | null}
                />
              );
            },
          },
        ],
      },
    ],
    [effectiveQuarter],
  );
}

function KpiCard({
  label,
  value,
  total,
  color,
}: {
  label: string;
  value: number;
  total: number;
  color: string;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <p className={`text-xl font-bold tabular-nums ${color}`}>
        {value}
        <span className="text-sm font-normal text-slate-400">/{total}</span>
      </p>
      <p className="text-[11px] text-slate-500">{label}</p>
    </div>
  );
}

type FilterChipProps = {
  active: boolean;
  label: string;
  onClick: () => void;
};

function FilterChip({ active, label, onClick }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
        active
          ? "border-blue-500 bg-blue-50 text-blue-700"
          : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700"
      }`}
    >
      {label}
    </button>
  );
}

const ALL_FUNDS_SELECTION: R1FundSelection = { mode: "all" };

export default function FundsPortfolioView() {
  const [selectedQuarter, setSelectedQuarter] = useState<string | null>(null);
  const [filterAllFiles, setFilterAllFiles] = useState(false);
  const [filterChaser1, setFilterChaser1] = useState(false);
  const [filterChaser2, setFilterChaser2] = useState(false);
  const [filterEscalation, setFilterEscalation] = useState(false);
  const [filterComments, setFilterComments] = useState(false);
  const [filterFirstProduction, setFilterFirstProduction] = useState(false);
  const [filterValidationStatus, setFilterValidationStatus] =
    useState<string>("");
  const { data: quarters = [] } = useQuery({
    queryKey: ["r1", "funds-portfolio-quarters", "all"],
    queryFn: () => r1Api.getFundsPortfolioQuarters(ALL_FUNDS_SELECTION),
    staleTime: 60_000,
  });

  const effectiveQuarter =
    selectedQuarter && quarters.includes(selectedQuarter)
      ? selectedQuarter
      : (quarters[0] ?? null);

  const { data, isLoading } = useQuery({
    queryKey: ["r1", "funds-portfolio", "all", effectiveQuarter],
    queryFn: () => r1Api.getFundsPortfolio(ALL_FUNDS_SELECTION, effectiveQuarter),
    enabled: effectiveQuarter !== null,
  });

  const rows = useMemo(
    () =>
      ((data?.data ?? []) as Row[]).sort((a, b) =>
        String(a.fund_name ?? "").localeCompare(String(b.fund_name ?? "")),
      ),
    [data],
  );

  const kpis = useMemo(() => {
    const total = rows.length;
    return {
      total,
      trrsReceived: rows.filter((r) => r.date_trr_received).length,
      pdfsReceived: rows.filter((r) => r.date_pdf_received).length,
      allFilesReceived: rows.filter(
        (r) => r.date_pdf_received && r.date_trr_received,
      ).length,
      validatedByQuantilia: rows.filter(
        (r) => r.step1_status === "validated",
      ).length,
      validatedByR1N1: rows.filter(
        (r) => r.step2_status === "validated",
      ).length,
      validatedByR1N2: rows.filter(
        (r) => r.step3_status === "validated",
      ).length,
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    let result = rows;
    if (filterAllFiles)
      result = result.filter((r) => r.date_pdf_received && r.date_trr_received);
    if (filterChaser1) result = result.filter((r) => r.date_chaser_1);
    if (filterChaser2) result = result.filter((r) => r.date_chaser_2);
    if (filterEscalation) result = result.filter((r) => r.date_escalation);
    if (filterComments) result = result.filter((r) => r.last_comment_message);
    if (filterFirstProduction) result = result.filter((r) => r.first_production === true || r.first_production === "true" || r.first_production === "Yes");
    if (filterValidationStatus)
      result = result.filter(
        (r) => r.validation_status === filterValidationStatus,
      );
    return result;
  }, [
    rows,
    filterAllFiles,
    filterChaser1,
    filterChaser2,
    filterEscalation,
    filterComments,
    filterFirstProduction,
    filterValidationStatus,
  ]);

  const columns = useColumns(effectiveQuarter);

  const hasActiveFilter =
    filterAllFiles ||
    filterChaser1 ||
    filterChaser2 ||
    filterEscalation ||
    filterComments ||
    filterFirstProduction ||
    !!filterValidationStatus;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        {quarters.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-400">Quarter</span>
            <Select
              size="sm"
              selectedKeys={
                effectiveQuarter ? new Set([effectiveQuarter]) : new Set()
              }
              onSelectionChange={(keys) =>
                setSelectedQuarter((Array.from(keys)[0] as string) ?? null)
              }
              aria-label="Portfolio quarter"
              classNames={{
                trigger:
                  "h-8 min-h-8 w-28 shadow-none !rounded-md border-slate-200 text-[12px]",
                value: "text-[12px]",
              }}
            >
              {quarters.map((q) => (
                <SelectItem key={q}>{q}</SelectItem>
              ))}
            </Select>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Loading…" color="primary" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between px-1">
            <h1 className="font-semibold text-xl text-primary">
              Portfolio Tracker
              {effectiveQuarter && (
                <span className="ml-2 text-sm font-normal text-slate-400">
                  — {effectiveQuarter}
                </span>
              )}
            </h1>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <KpiCard
              label="TRRs received"
              value={kpis.trrsReceived}
              total={kpis.total}
              color="text-slate-800"
            />
            <KpiCard
              label="PDFs received"
              value={kpis.pdfsReceived}
              total={kpis.total}
              color="text-slate-800"
            />
            <KpiCard
              label="All files received"
              value={kpis.allFilesReceived}
              total={kpis.total}
              color="text-emerald-700"
            />
            <KpiCard
              label="Validated by Quantilia"
              value={kpis.validatedByQuantilia}
              total={kpis.total}
              color="text-blue-700"
            />
            <KpiCard
              label="R1 Validation N°1"
              value={kpis.validatedByR1N1}
              total={kpis.total}
              color="text-purple-700"
            />
            <KpiCard
              label="R1 Validation N°2"
              value={kpis.validatedByR1N2}
              total={kpis.total}
              color="text-violet-700"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-slate-400">Filters</span>
            <FilterChip
              active={filterAllFiles}
              label="All files received"
              onClick={() => setFilterAllFiles((v) => !v)}
            />
            <FilterChip
              active={filterChaser1}
              label="Chaser 1 sent"
              onClick={() => setFilterChaser1((v) => !v)}
            />
            <FilterChip
              active={filterChaser2}
              label="Chaser 2 sent"
              onClick={() => setFilterChaser2((v) => !v)}
            />
            <FilterChip
              active={filterEscalation}
              label="Escalation sent"
              onClick={() => setFilterEscalation((v) => !v)}
            />
            <FilterChip
              active={filterComments}
              label="Has comments"
              onClick={() => setFilterComments((v) => !v)}
            />
            <FilterChip
              active={filterFirstProduction}
              label="First Production"
              onClick={() => setFilterFirstProduction((v) => !v)}
            />
            <Select
              size="sm"
              placeholder="Validation status"
              selectedKeys={
                filterValidationStatus
                  ? new Set([filterValidationStatus])
                  : new Set()
              }
              onSelectionChange={(keys) => {
                const val = Array.from(keys)[0] as string | undefined;
                setFilterValidationStatus(val ?? "");
              }}
              aria-label="Validation status filter"
              classNames={{
                trigger:
                  "h-7 min-h-7 w-44 shadow-none !rounded-full border-slate-200 text-[12px]",
                value: "text-[12px]",
              }}
            >
              <SelectItem key="pending">Pending</SelectItem>
              <SelectItem key="validated">Validated</SelectItem>
              <SelectItem key="rejected">Rejected</SelectItem>
            </Select>
            {hasActiveFilter && (
              <button
                type="button"
                className="text-xs text-slate-400 hover:text-slate-600 underline"
                onClick={() => {
                  setFilterAllFiles(false);
                  setFilterChaser1(false);
                  setFilterChaser2(false);
                  setFilterEscalation(false);
                  setFilterComments(false);
                  setFilterValidationStatus("");
                }}
              >
                Clear all
              </button>
            )}
            <span className="ml-auto rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-medium text-gray-500 shadow-sm">
              {filteredRows.length.toLocaleString()}
              {hasActiveFilter && rows.length !== filteredRows.length
                ? ` / ${rows.length}`
                : ""}{" "}
              rows
            </span>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] overflow-hidden">
            <R1DataTable<Row, unknown>
              table_name="r1-funds-portfolio"
              columns={columns}
              data={filteredRows}
              getRowClassName={() => "hover:!bg-slate-50"}
              pinnedLeft={["r1_gp_id_fonds", "fund_name"]}
              groupHeaderClassName="!p-0"
              noCard
            />
            <div className="flex items-center border-t border-slate-200 px-4 py-3">
              <DownloadButton
                href={buildDownloadUrl(effectiveQuarter)}
                filename={`r1_funds_portfolio_${new Date().toISOString().replace(/[:.]/g, "-")}.xlsx`}
                size="sm"
                text="Download"
                className="text-sm"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
